import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import isURL from "validator/lib/isURL";
import { useSnapshot } from "valtio";
import type {
  AssignmentInfo,
  Client,
  ClientWithContact,
  Lead,
  ParsedAssignmentData,
} from "../../../../client";
import { LoadingSpinner } from "../../../../components";
import {
  useCreateLead,
  useGetClient,
  useGetClients,
  useGetLead,
  useUpdateClient,
  useUpdateLead,
} from "../../../../services";
import { useStatsigClient } from "../../../../hooks";
import { useRequestAssignmentInfo } from "../../../../services/organtization/request-assigment-info";
import {
  useSemanticMatchWithProgress,
  useSemanticMatchProfile,
} from "../../../../services/organtization/semantic-match";
import { jsonParse } from "../../../../services/utils/parse";
import { type LeadRowActiveState, homeState } from "../../../../state/home";
import { useToggleActiveState } from "../utils/use-toggle-active-state";
import { AnimatedPanel, EventsPanel, MemberPanel, QRPanel } from "./panel";
import { AssignmentPanel } from "./panel/AssignmentPanel";
import { AssignmentWithMatchPanel } from "./panel/AssignmentWithMatchPanel";
import { QuickAssignmentInput } from "./panel/QuickAssignmentInput";
import { SemanticMatchResultsList } from "./panel/SemanticMatchResultsList";
import { useRequestClientInfo } from "../../../../services/organtization/client/request-client-info";
import {
  createGenerateAssignmentOptions,
  useGenerateAssignment,
} from "../../../../services/organtization/lead/generate-assignment";
import { getLeadsOptions } from "../../../../services/organtization/lead/get-leads";
import { ChildLeadRow } from "./ChildLeadRow";

interface Props {
  organizationId: string;
  leadId?: string;
  state: LeadRowActiveState;
  divider?: boolean;
  className?: string;
  onMatchingMembers: (numberOfMatchingMembers: number) => void;
  onLeadCreated: () => void;
  profileId?: string;
  onAssignProfile?: (profileId: string) => void;
  hideAssignment?: boolean;
  hideProfileList?: boolean;
  childLeads?: Lead[];
  onArchive?: () => void;
  isCreatingChildLead?: boolean;
}

const blankLead: Lead = {
  id: "",
  clientId: "",
  title: "",
  description: "",
  contactPersonName: "",
  contactPersonEmail: "",
  contactPersonPhone: "",
  contractId: "",
  status: "",
  numberOfUnreadEvents: 0,
  // Will always get overridden before being sent to the backend
  orgId: "",
  profileVersions: [],
};

const clientWithContactToClient = (client: ClientWithContact): Client => ({
  ...client,
  description: client.description ?? "",
  url: client.url ?? "",
});

export const LeadPanelContent = ({
  organizationId,
  leadId,
  state,
  divider,
  className,
  onMatchingMembers,
  onLeadCreated,
  profileId,
  onAssignProfile,
  hideAssignment,
  hideProfileList,
  childLeads,
  onArchive,
  isCreatingChildLead,
}: Props) => {
  const { selectedLead } = useSnapshot(homeState);
  const statsigClient = useStatsigClient();
  const queryClient = useQueryClient();

  const updateLead = useUpdateLead({}, profileId);
  const updateClient = useUpdateClient();
  const {
    run: runSemanticMatch,
    isMatching: isSseMatching,
    progress: sseProgress,
  } = useSemanticMatchWithProgress(organizationId, leadId ?? "");
  const [matchingForProfileIds, setMatchingForProfileIds] = useState<string[]>([]);
  const semanticMatchProfile = useSemanticMatchProfile({
    onSettled: () => setMatchingForProfileIds([]),
  });
  const { scrollToLead } = useToggleActiveState({ leadId: leadId ?? null });

  const [editingAssignmentLink, setEditingAssignmentLink] = useState(false);
  const [editingWebsiteLink, setEditingWebsiteLink] = useState(false);
  const [couldNotLoadFromUrl, setCouldNotLoadFromUrl] = useState(false);
  const [justParsedTitle, setJustParsedTitle] = useState<string | null>(null);
  const [couldNotLoadClientFromUrl, setCouldNotClientLoadFromUrl] =
    useState(false);

  const createLead = useCreateLead({
    onSuccess: (lead) => {
      const newLead = jsonParse<Lead>(lead);

      if (selectedLead) {
        // Navigate to the new lead
        homeState.selectedLead = {
          leadId: newLead.id ?? "",
          state,
        };
        onLeadCreated();
        scrollToLead(newLead.id ?? "");
      }
    },
  });

  const { data: lead } = useGetLead(
    { organizationID: organizationId, leadID: leadId ?? "" },
    {
      enabled: !!leadId,
    },
  );

  const { data: clients } = useGetClients(organizationId);

  const { data: client } = useGetClient({
    clientId: updateLead.variables?.body.clientId ?? lead?.clientId ?? "",
    organizationID: organizationId,
  });

  const onMutateLead = useCallback(
    (payload: Partial<Lead>) => {
      if (lead) {
        // Update existing lead if it exists
        updateLead.mutate({
          path: { organizationID: organizationId, leadID: lead.id ?? "" },
          body: { ...lead, ...payload },
        });
      } else {
        // Create new lead if it doesn't exist

        createLead.mutate({
          body: {
            ...blankLead,
            orgId: organizationId,
            ...payload,
          },
          path: {
            organizationID: organizationId,
          },
        });
      }
    },
    [lead, updateLead, organizationId, createLead],
  );

  const onMutateClient = useCallback(
    (payload: Partial<Client>) => {
      if (!client) return;

      updateClient.mutate({
        path: { organizationID: organizationId, clientID: client.id },
        body: { ...clientWithContactToClient(client), ...payload },
      });
    },
    [updateClient, organizationId, client],
  );

  const requestInfoToastId = useRef<string | number>();
  const profileVersionsAtMatchRef = useRef<string[]>([]);

  const requestWebsiteClientInfo = useRequestClientInfo({
    onMutate: () => {
      requestInfoToastId.current = toast.loading(
        "Getting info about assignment...",
      );
    },
    onSuccess: (clientInfoResponse, { query: { url } }) => {
      toast.success("Updated company info", {
        id: requestInfoToastId.current,
      });

      setEditingWebsiteLink(false);

      if (clientInfoResponse.failedToLoadData) {
        setCouldNotClientLoadFromUrl(true);
      } else {
        onMutateLead({
          companyName: clientInfoResponse.client?.name,
          companyUrl: url,
          companyDesc: clientInfoResponse.client?.description,
          logoSuggestions: clientInfoResponse.client?.logoSuggestions,
        });
      }
    },
    onError: () => {
      toast.error("Something went wrong", { id: requestInfoToastId.current });
    },
  });

  const requestAssignmentInfo = useRequestAssignmentInfo({
    onMutate: () => {
      requestInfoToastId.current = toast.loading(
        "Getting info about assignment...",
      );
    },
    onSuccess: (assignmentInfo, { body: { url, info } }) => {
      try {
        const parsedInfo = jsonParse<AssignmentInfo>(assignmentInfo);
        setCouldNotLoadFromUrl(false);

        if (parsedInfo.couldNotLoadFromUrl) {
          toast.success("Assignment info fetch failed", {
            id: requestInfoToastId.current,
          });
          setCouldNotLoadFromUrl(true);
        } else {
          setEditingAssignmentLink(false);
          toast.success("Updated assignment info", {
            id: requestInfoToastId.current,
          });

          let currentTitle = parsedInfo.title;

          if (lead && lead.assignmentInfo && lead.assignmentInfo.title) {
            currentTitle = lead.assignmentInfo.title;
          }

          onMutateLead({
            assignmentInfo: {
              ...parsedInfo,
              title: currentTitle,
              assignmentUrl: url ?? "",
            },
          });
        }
      } catch (error) {
        toast.error("Failed to parse assignment information", {
          id: requestInfoToastId.current,
        });
        // Optionally set editing link back to false here if needed
        setEditingAssignmentLink(false);
      }
    },
    onError: () => {
      toast.error("Something went wrong", { id: requestInfoToastId.current });
    },
  });

  const generateAssignmentInfo = useGenerateAssignment({
    onMutate: () => {
      requestInfoToastId.current = toast.loading(
        "Generating assignment information...",
      );
    },
    onSuccess: (parsedInfo: ParsedAssignmentData) => {
      setCouldNotLoadFromUrl(false);
      setEditingAssignmentLink(false);
      toast.success("Assignment information generated successfully", {
        id: requestInfoToastId.current,
      });

      setJustParsedTitle(parsedInfo.title || null);

      // Optimistically update the lead in the query cache so the UI
      // shows the parsed data immediately (no flash between loading and data).
      if (leadId) {
        const queryKey = getLeadsOptions(organizationId).queryKey;
        queryClient.setQueryData<Lead[]>(queryKey, (oldLeads) =>
          oldLeads?.map((l) =>
            l.id === leadId
              ? {
                  ...l,
                  parsedAssignmentData: parsedInfo,
                  title: parsedInfo.title || l.title,
                }
              : l,
          ),
        );

        const profileVersions = profileVersionsAtMatchRef.current;
        if (profileVersions.length > 0) {
          setMatchingForProfileIds(profileVersions);
          profileVersions.forEach((profileId) => {
            semanticMatchProfile.mutate({
              path: { organizationID: organizationId, profileID: profileId },
              body: { leadId },
            });
          });
        } else {
          runSemanticMatch();
        }
      }
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to generate assignment: ${errorMessage}`, {
        id: requestInfoToastId.current,
      });
      setEditingAssignmentLink(false);
    },
  });

  const leadProfileId =
    updateLead.variables?.body.profileId ?? lead?.profileId ?? undefined;

  // When onAssignProfile is provided (grouped mode), intercept profileId mutations
  // so they create a child lead instead of updating the parent.
  const effectiveMutateLead = useCallback(
    (payload: Partial<Lead>) => {
      if (onAssignProfile && "profileId" in payload && payload.profileId) {
        onAssignProfile(payload.profileId);
        return;
      }
      onMutateLead(payload);
    },
    [onAssignProfile, onMutateLead],
  );

  const child = useMemo(() => {
    switch (state) {
      case "assignment": {
        if (hideAssignment) return null;
        const assignmentPanelProps = {
          disabled: leadId
            ? generateAssignmentInfo.isPending
            : requestAssignmentInfo.isPending,
          onRequestAssignmentInfo: async (url: string) => {
            if (isURL(url)) {
              if (leadId && !generateAssignmentInfo.isPending) {
                generateAssignmentInfo.mutate(
                  createGenerateAssignmentOptions(organizationId, leadId, {
                    url,
                  }),
                );
              } else if (!leadId && !requestAssignmentInfo.isPending) {
                try {
                  await requestAssignmentInfo.mutateAsync({
                    body: { url, info: "" },
                  });
                } catch (error) {
                  console.error("Assignment info request failed:", error);
                }
              }
            }
          },
          onRequestAssignmentInfoWithInfo: (info: string) => {
            if (leadId && !generateAssignmentInfo.isPending) {
              profileVersionsAtMatchRef.current = (childLeads ?? [])
                .map((cl) => cl.profileId)
                .filter((id): id is string => Boolean(id));
              generateAssignmentInfo.mutate(
                createGenerateAssignmentOptions(organizationId, leadId, {
                  info,
                }),
              );
            } else if (!leadId) {
              requestAssignmentInfo.mutate({ body: { info, url: "" } });
            }
          },
          onRequestWebsiteInfo: (url: string) => {
            if (isURL(url) && !requestWebsiteClientInfo.isPending) {
              requestWebsiteClientInfo.mutate({ query: { url } });
            }
          },
          onSelectLogo: (logoUrl: string) => {
            if (logoUrl === "") {
              onMutateLead({
                companyLogoId: undefined,
                logoSuggestionOrigin: undefined,
              });
            } else {
              onMutateLead({
                companyLogoId: logoUrl,
              });
            }
          },
          lead,
          onMutateLead: effectiveMutateLead,
          isEditingWebsiteLink: editingWebsiteLink,
          isEditingAssignmentLink: editingAssignmentLink,
          onEditWebsiteLinkClick: () => setEditingWebsiteLink(true),
          onEditAssigmentLinkClick: () => setEditingAssignmentLink(true),
          couldNotLoadFromUrl,
          organizationId,
          onMatchingMembers,
          couldNotLoadClientFromUrl,
        };

        const isNewLayout =
          leadId &&
          statsigClient?.checkGate("semantic_match_layout") &&
          !lead?.profileId;

        const hasAssignmentData =
          (lead?.parsedAssignmentData?.skills && !couldNotLoadFromUrl) ||
          ((lead?.assignmentInfo?.skills?.length ?? 0) > 0 &&
            !couldNotLoadFromUrl);

        if (isNewLayout) {
          if (!hasAssignmentData) {
            return (
              <>
                {childLeads !== undefined && (childLeads.length > 0 || isCreatingChildLead) && (
                  <ChildLeadsSection
                    childLeads={childLeads}
                    organizationId={organizationId}
                    isCreatingChildLead={isCreatingChildLead}
                  />
                )}
                <QuickAssignmentInput
                  onSubmit={(text) =>
                    assignmentPanelProps.onRequestAssignmentInfoWithInfo(text)
                  }
                  onCancel={() => {
                    onMutateLead({ status: "archived" });
                    homeState.selectedLead = { leadId: leadId ?? null, state: false };
                  }}
                  isPending={assignmentPanelProps.disabled}
                />
              </>
            );
          } else {
            return (
              <>
                {childLeads !== undefined && (childLeads.length > 0 || isCreatingChildLead) && (
                  <ChildLeadsSection
                    childLeads={childLeads}
                    organizationId={organizationId}
                    isCreatingChildLead={isCreatingChildLead}
                  />
                )}
                <SemanticMatchResultsList
                  organizationId={organizationId}
                  leadId={leadId}
                  onMutateLead={effectiveMutateLead}
                  childLeads={childLeads}
                  isParsingAssignment={assignmentPanelProps.disabled}
                  hasAssignment={true}
                  revealTitle={justParsedTitle ?? undefined}
                  onRevealDone={() => setJustParsedTitle(null)}
                  matchingProfileIds={
                    matchingForProfileIds.length > 0 ? matchingForProfileIds : undefined
                  }
                  isProfilePage={hideProfileList || !!profileId}
                  externalIsMatching={isSseMatching}
                  matchProgress={isSseMatching ? sseProgress : undefined}
                />
              </>
            );
          }
        }

        // Use the new match panel only for true assignment leads (no profileId).
        // Old-style leads (profileId set directly on the lead) always use the old panel.
        const assignmentPanel =
          leadId &&
          statsigClient?.checkGate("semantic_match_layout") &&
          !lead?.profileId ? (
            <AssignmentWithMatchPanel
              {...assignmentPanelProps}
              leadId={leadId}
              childLeads={childLeads}
              hideProfileList={hideProfileList}
              onArchive={
                onArchive ?? (() => onMutateLead({ status: "archived" }))
              }
            />
          ) : (
            <AssignmentPanel {...assignmentPanelProps} />
          );

        return (
          <>
            {childLeads !== undefined && childLeads.length > 0 && (
              <ChildLeadsSection
                childLeads={childLeads}
                organizationId={organizationId}
              />
            )}
            {assignmentPanel}
          </>
        );
      }
      case "member":
        return (
          <MemberPanel
            profileId={leadProfileId}
            profileVersions={lead?.profileVersions ?? []}
            onMutateLead={effectiveMutateLead}
            profileLinkId={lead?.profileLinkId}
            leadId={lead?.id}
            lead={lead}
            profileCanBeReplaced={!lead?.contractId}
          />
        );
      case "qr":
        return (
          <QRPanel profileLinkId={lead?.profileLinkId} leadId={lead?.id} />
        );
      case "events":
        return (
          <EventsPanel
            organizationId={organizationId}
            leadId={lead?.id}
            events={lead?.events ?? []}
            onResetNumberOfUnreadEvents={() =>
              onMutateLead({ numberOfUnreadEvents: 0 })
            }
          />
        );
      default:
        return null;
    }
  }, [
    state,
    client,
    clients,
    lead,
    onMutateLead,
    onMutateClient,
    requestAssignmentInfo,
    leadProfileId,
    organizationId,
    statsigClient,
    leadId,
    effectiveMutateLead,
    hideAssignment,
    childLeads,
    couldNotLoadFromUrl,
    justParsedTitle,
    isSseMatching,
    sseProgress,
    isCreatingChildLead,
  ]);

  return (
    <AnimatedPanel state={state} className={className}>
      {divider && <div className="w-full h-px bg-gray-2" />}
      <div className="py-4 lg:px-4">
        <Suspense fallback={<Loading />}>{child}</Suspense>
      </div>
    </AnimatedPanel>
  );
};

const Loading = () => (
  <div className="flex justify-center items-center h-full w-full">
    <LoadingSpinner size="small" />
  </div>
);

const ChildLeadsSection = ({
  childLeads,
  organizationId,
  isCreatingChildLead,
}: {
  childLeads: Lead[];
  organizationId: string;
  isCreatingChildLead?: boolean;
}) => {
  const [showRow, setShowRow] = useState(false);
  const [isDone, setIsDone] = useState(false);
  // IDs known before creation started — freeze visible set during loading
  const knownIdsRef = useRef<Set<string>>(new Set());
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (isCreatingChildLead) {
      knownIdsRef.current = new Set(childLeads.map((c) => c.id).filter(Boolean) as string[]);
      setShowRow(true);
      setIsDone(false);
      startTimeRef.current = Date.now();
    } else if (showRow && !isDone) {
      const elapsed = Date.now() - (startTimeRef.current ?? Date.now());
      const remaining = Math.max(0, 3000 - elapsed);
      const t = setTimeout(() => {
        setIsDone(true);
        setTimeout(() => {
          setShowRow(false);
          knownIdsRef.current = new Set();
        }, 600);
      }, remaining);
      return () => clearTimeout(t);
    }
  }, [isCreatingChildLead]);

  // While the loading row is visible, only show leads that existed before creation
  const displayedLeads = showRow
    ? childLeads.filter((c) => c.id && knownIdsRef.current.has(c.id))
    : childLeads;

  return (
    <div className="flex flex-col gap-2 mb-6">
      {displayedLeads.map((child, i) => (
        <ChildLeadRow
          key={child.id}
          lead={child}
          organizationId={organizationId}
          index={i}
        />
      ))}
      {showRow && <CreatingProfileVersionRow isDone={isDone} />}
    </div>
  );
};

const PROGRESS_GRADIENT = "#000000";

const CreatingProfileVersionRow = ({ isDone }: { isDone: boolean }) => {
  const barRef = useRef<HTMLDivElement>(null);
  const [barStyle, setBarStyle] = useState<React.CSSProperties>({
    background: PROGRESS_GRADIENT,
    animation: "profileVersionProgress 6s linear forwards",
  });
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!isDone) return;

    // Capture current fill width before stopping animation
    const bar = barRef.current;
    const container = bar?.parentElement;
    const currentPct =
      bar && container
        ? (bar.getBoundingClientRect().width / container.getBoundingClientRect().width) * 100
        : 0;

    // Lock at current position, then animate to 100%
    setBarStyle({ background: PROGRESS_GRADIENT, width: `${currentPct}%` });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setBarStyle({ background: PROGRESS_GRADIENT, width: "100%", transition: "width 0.3s ease-out" });
      });
    });

    const t = setTimeout(() => setFading(true), 500);
    return () => clearTimeout(t);
  }, [isDone]);

  return (
    <div
      className={`rounded-2xl border border-gray-200 overflow-hidden transition-opacity duration-300${fading ? " opacity-0" : ""}`}
    >
      <style>{`
        @keyframes profileVersionProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
      <div className="flex items-center gap-3 py-3 pr-3 pl-4">
        <div className="w-12 h-12 rounded-xl bg-gray-100 shrink-0" />
        <div className="flex flex-col gap-1.5 flex-1 min-w-0">
          <div className="h-3 w-32 rounded-full bg-gray-100 overflow-hidden">
            <div ref={barRef} className="h-full rounded-full" style={barStyle} />
          </div>
          <span className="text-xs text-gray-400">Creating profile version...</span>
        </div>
      </div>
    </div>
  );
};
